// Configuration PUTs replace the whole document. Serialize them, keeping only the
// newest waiting snapshot, so an older response cannot overwrite a newer edit.
export function createEditorSaveQueue(write) {
  let running = false;
  let pending = null;
  let lastError = null;
  const drainWaiters = [];

  async function pump() {
    if (running) return;
    running = true;
    while (pending) {
      const job = pending;
      pending = null;
      try {
        await write(job.snapshot);
        lastError = null;
        job.waiters.forEach(({ resolve }) => resolve());
      } catch (error) {
        lastError = error;
        job.waiters.forEach(({ reject }) => reject(error));
      }
    }
    running = false;
    drainWaiters.splice(0).forEach(({ resolve, reject }) => lastError ? reject(lastError) : resolve());
  }

  return {
    save(config) {
      // Freeze the wire snapshot now; later UI mutations must not alter a queued PUT.
      const snapshot = JSON.parse(JSON.stringify(config));
      const result = new Promise((resolve, reject) => {
        const waiters = pending?.waiters || [];
        waiters.push({ resolve, reject });
        pending = { snapshot, waiters };
      });
      void pump();
      return result;
    },
    flush() {
      if (!running) return lastError ? Promise.reject(lastError) : Promise.resolve();
      return new Promise((resolve, reject) => drainWaiters.push({ resolve, reject }));
    },
    waitForIdle() {
      // Reopening after a failed write must still be able to load the server's last
      // saved version. Save All uses flush() instead, which reports persistence failure.
      if (!running) return Promise.resolve();
      return new Promise(resolve => drainWaiters.push({ resolve, reject: resolve }));
    },
  };
}

// Retain queue identity across SPA unmount/reopen. A new editor for the same form
// must wait for writes started by its previous instance before fetching configuration.
// Idle queues retain no snapshots, only their writer and last error.
export function createEditorSaveQueueRegistry() {
  const queues = new Map();
  return (formId, write) => {
    if (!queues.has(formId)) queues.set(formId, createEditorSaveQueue(write));
    return queues.get(formId);
  };
}

export const getEditorSaveQueue = createEditorSaveQueueRegistry();
